<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { email as emailValidator } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { debounce } from '@chatwoot/utils';
import { useStore } from 'dashboard/composables/store';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import FormSectionHeader from 'dashboard/components-next/Forms/FormSectionHeader.vue';
import CompanyCustomAttributeItem from 'dashboard/components-next/Companies/CompanyDetail/CompanyCustomAttributeItem.vue';
import CompanyLabels from 'dashboard/components-next/Companies/CompanyDetail/CompanyLabels.vue';

const props = defineProps({
  companyData: { type: Object, default: null },
  isNewCompany: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  domain: '',
  description: '',
  email: '',
  phoneNumber: '',
  cnpj: '',
  city: '',
  countryCode: '',
  contactId: null,
  customAttributes: {},
  socialProfiles: {
    facebook: '',
    github: '',
    instagram: '',
    telegram: '',
    tiktok: '',
    linkedin: '',
    twitter: '',
  },
};

const state = reactive({ ...defaultState });
const selectedLabels = ref([]);
const handleLabelsUpdate = labels => {
  selectedLabels.value = labels;
};

const validationRules = {
  name: { required },
  email: { emailValidator },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const { orderedFields } = useFieldOrder('company');

// CNPJ: aviso de formato não-bloqueante (não entra em isFormInvalid; não desabilita o salvar)
// só aparece depois que o usuário sai do campo (blur)
const CNPJ_FORMAT = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/;
const cnpjTouched = ref(false);
const hasCnpjFormatWarning = computed(() => {
  if (!cnpjTouched.value) return false;
  const value = (state.cnpj || '').trim();
  return value.length > 0 && !CNPJ_FORMAT.test(value);
});

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    icon,
    placeholder: t(
      `COMPANIES_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const label = key =>
  t(`COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.LABEL`);
const placeholder = key =>
  t(`COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`);

const customAttributeWithValue = attribute => ({
  ...attribute,
  value: state.customAttributes?.[attribute.attributeKey] ?? '',
});

const syncState = () => {
  if (props.isNewCompany) return;
  const {
    id,
    name = '',
    domain = '',
    description = '',
    email = '',
    phoneNumber = '',
    cnpj = '',
    city = '',
    countryCode = '',
    customAttributes = {},
    socialProfiles = {},
  } = props.companyData || {};

  Object.assign(state, {
    id,
    name,
    domain,
    description,
    email,
    phoneNumber,
    cnpj,
    city,
    countryCode,
    customAttributes: { ...customAttributes },
    socialProfiles: {
      ...defaultState.socialProfiles,
      ...socialProfiles,
    },
  });
};

const handleCountrySelection = value => {
  state.countryCode = value || '';
  emit('update', { ...state });
};

const handleCustomAttributesUpdate = value => {
  state.customAttributes = value;
  emit('update', { ...state });
};

const handleCompanyAttributeUpdate = (attributeKey, value) => {
  handleCustomAttributesUpdate({
    ...state.customAttributes,
    [attributeKey]: value,
  });
};

const resetValidation = () => v$.value.$reset();

const store = useStore();
const contactOptions = ref([]);

const resetForm = () => {
  Object.assign(state, defaultState);
  contactOptions.value = [];
  selectedLabels.value = [];
};

const contactsFromStore = () => {
  const results = store.getters['contacts/getContactsList'] || [];
  contactOptions.value = results.map(c => ({
    value: c.id,
    label: c.name || c.email || String(c.id),
  }));
};

const loadContacts = async () => {
  await store.dispatch('contacts/get', { page: 1, sortAttr: 'name' });
  contactsFromStore();
};

const searchContacts = debounce(async query => {
  if (!query || query.length < 1) {
    await loadContacts();
    return;
  }
  await store.dispatch('contacts/search', { search: query, page: 1 });
  contactsFromStore();
}, 300);

const handleContactSelect = value => {
  state.contactId = value;
  emit('update', { ...state });
};

const setContactId = contactId => {
  state.contactId = contactId;
  emit('update', { ...state });
};

const validate = async () => {
  v$.value.$touch();
  return v$.value.$validate();
};

watch(
  () => props.companyData?.id,
  id => {
    if (id) syncState();
  },
  { immediate: true }
);

defineExpose({
  state,
  selectedLabels,
  isFormInvalid,
  resetValidation,
  resetForm,
  validate,
  contactOptions,
  searchContacts,
  setContactId,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <template v-for="(field, index) in orderedFields" :key="field.id">
        <div
          v-if="field.showGroupHeader"
          class="sm:col-span-2"
          :class="{ 'mt-2': index > 0 }"
        >
          <FormSectionHeader :title="t(field.groupTitleKey)" uppercase />
        </div>
        <div :class="{ 'sm:col-span-2': field.fullWidth }">
          <CompanyLabels
            v-if="field.type === 'standard' && field.key === 'labels'"
            :company-id="state.id || null"
            :defer-persist="isNewCompany"
            :selected-labels="selectedLabels"
            @update:selected-labels="handleLabelsUpdate"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'name'"
            v-model="state.name"
            :label="label('NAME')"
            :placeholder="placeholder('NAME')"
            :message="
              v$.name.$error
                ? t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.NAME.ERROR')
                : ''
            "
            :message-type="v$.name.$error ? 'error' : 'info'"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @input="v$.name.$touch()"
            @blur="
              v$.name.$touch();
              emit('update', { ...state });
            "
            @change="emit('update', { ...state })"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'domain'"
            v-model="state.domain"
            :label="label('DOMAIN')"
            :placeholder="placeholder('DOMAIN')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @blur="emit('update', { ...state })"
            @change="emit('update', { ...state })"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'email'"
            v-model="state.email"
            :label="label('EMAIL')"
            :placeholder="placeholder('EMAIL')"
            :message="
              v$.email.$error
                ? t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL.ERROR')
                : ''
            "
            :message-type="v$.email.$error ? 'error' : 'info'"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @input="v$.email.$touch()"
            @blur="
              v$.email.$touch();
              emit('update', { ...state });
            "
            @change="emit('update', { ...state })"
          />
          <div
            v-else-if="
              field.type === 'standard' && field.key === 'phone_number'
            "
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              label('PHONE_NUMBER')
            }}</span>
            <PhoneNumberInput
              v-model="state.phoneNumber"
              :placeholder="placeholder('PHONE_NUMBER')"
              @change="emit('update', { ...state })"
            />
          </div>
          <Input
            v-else-if="field.type === 'standard' && field.key === 'cnpj'"
            v-model="state.cnpj"
            :label="label('CNPJ')"
            :placeholder="placeholder('CNPJ')"
            :message="
              hasCnpjFormatWarning
                ? t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CNPJ.WARNING')
                : ''
            "
            :message-type="hasCnpjFormatWarning ? 'error' : 'info'"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @blur="
              cnpjTouched = true;
              emit('update', { ...state });
            "
            @change="emit('update', { ...state })"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'city'"
            v-model="state.city"
            :label="label('CITY')"
            :placeholder="placeholder('CITY')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @blur="emit('update', { ...state })"
            @change="emit('update', { ...state })"
          />
          <div
            v-else-if="field.type === 'standard' && field.key === 'country'"
            class="relative flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              label('COUNTRY')
            }}</span>
            <ComboBox
              v-model="state.countryCode"
              :options="countryOptions"
              :placeholder="placeholder('COUNTRY')"
              class="[&>div>button]:h-8 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent"
              @update:model-value="handleCountrySelection"
            />
            <button
              v-if="state.countryCode"
              type="button"
              class="absolute right-8 bottom-1 p-0.5 text-n-slate-9 hover:text-n-slate-12 transition-colors z-10"
              @click="handleCountrySelection('')"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <Input
            v-else-if="field.type === 'standard' && field.key === 'description'"
            v-model="state.description"
            :label="label('DESCRIPTION')"
            :placeholder="placeholder('DESCRIPTION')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
            @blur="emit('update', { ...state })"
            @change="emit('update', { ...state })"
          />
          <div
            v-else-if="
              field.type === 'standard' && field.key === 'social_profiles'
            "
            class="flex flex-wrap gap-2"
          >
            <div
              v-for="item in socialProfilesForm"
              :key="item.key"
              class="flex items-center h-8 gap-2 px-2 rounded-lg bg-n-alpha-2 dark:bg-n-solid-3"
            >
              <Icon
                :icon="item.icon"
                class="flex-shrink-0 text-n-slate-11 size-4"
              />
              <input
                v-model="state.socialProfiles[item.key.toLowerCase()]"
                class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
                :placeholder="item.placeholder"
                :size="item.placeholder.length"
                @input="emit('update', { ...state })"
              />
            </div>
          </div>
          <CompanyCustomAttributeItem
            v-else-if="field.type === 'custom'"
            :attribute="customAttributeWithValue(field.attribute)"
            @update="handleCompanyAttributeUpdate"
          />
        </div>
      </template>
      <div v-if="isNewCompany" class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.CONTACT_LINK.LABEL')
        }}</span>
        <ComboBox
          v-model="state.contactId"
          :options="contactOptions"
          :placeholder="
            t(
              'COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.CONTACT_LINK.PLACEHOLDER'
            )
          "
          :search-placeholder="
            t(
              'COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.CONTACT_LINK.PLACEHOLDER'
            )
          "
          :empty-state="
            t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.CONTACT_LINK.NO_RESULTS')
          "
          @search="searchContacts"
          @update:model-value="handleContactSelect"
        />
        <slot name="contact-link-action" />
      </div>
    </div>
  </div>
</template>
